import { Period, UnixTime } from 'dashboard/lib/unixTime';
import { FilterOptions } from 'dashboard/models/metrics-verification';
import { Verification } from 'dashboard/models/verification';
import { InquiryGranularities } from 'dashboard/redux/reducers/metrics-inquiry';
import { Iso8601DateString } from 'lib/types';
import { CheckName } from 'lib/verificationCheck';

export interface MetricsVolumeSummary {
  total: number;

  started: number;
  'started-rate': number;
  pending: number;
  'pending-rate': number;
  'pending-and-expired': number;
  'pending-and-expired-rate': number;

  completed: number;
  'completed-rate': number;
  failed: number;
  'failed-rate': number;
  expired: number;
  'expired-rate': number;

  'completed-and-needs-review': number;
  'completed-and-needs-review-rate': number;
  'completed-and-approved': number;
  'completed-and-approved-rate': number;
  'completed-and-declined': number;
  'completed-and-declined-rate': number;

  'needs-review': number;
  'needs-review-rate': number;
  approved: number;
  'approved-rate': number;
  declined: number;
  'declined-rate': number;
}

export enum MetricsGroupBy {
  Accounts = 'accounts',
}

export interface MetricsTemplateFunnel {
  data: TemplateFunnelDatum[];
}

export interface TemplateFunnelDatum {
  type: FunnelStepType;
  status: FunnelStepStatus;
  count: number;
  dropoff: number;
  fraction_completed: number;
}

export type FunnelStepType = Verification['type'] | 'created' | 'completed';
export type FunnelStepStatus = null | 'attempted' | 'passed';

export interface MetricsVerificationCheckSummary {
  data: VerificationCheckDatum[];
}

export interface MetricsVerificationChecksOverTime {
  data: MetricsVerificationChecksOverTimeDatum[];
  domain: [UnixTime | null, UnixTime];
  period: Period;
}

export interface MetricsVerificationChecksOverTimeMetaData
  extends MetricsVerificationChecksOverTime {
  filter_options: FilterOptions;
}

export interface MetricsVerificationChecksOverTimeResponse {
  meta: MetricsVerificationChecksOverTimeMetaData;
  updated_at: Iso8601DateString;
}

export interface MetricsVerificationChecksOverTimeDatum {
  type: Verification['type'];
  updated_at: Iso8601DateString;
  data: VerificationCheckV2TypeDatum[];
}

export interface VerificationCheckV2TypeDatum {
  data: VerificationCheckV2Datum[];
  check_required: boolean;
  name: CheckName;
}

export interface VerificationCheckLineChartDatum extends Partial<Record<CheckName, number>> {
  created_timestamp: number;
}

export interface VerificationCheckV2Metrics {
  pass_count: number;
  failure_count: number;
  not_applicable_count?: number;
}

export interface VerificationCheckV2Datum extends VerificationCheckV2Metrics {
  created_timestamp: UnixTime;
}

export interface VerificationCheckRateDatum {
  created_timestamp: UnixTime;
  [key: string]: number;
}

export interface VerificationCheckDatum {
  count: number;
  type: Verification['type'];
  check_name: CheckName;
}

export interface MetricsTemplateFunnelFlowStep {
  id: string;
  name: string;
  metrics: {
    value: number;
    percentage: number;
  };
}

export interface MetricsInquiryVolumeMetrics {
  created: number;
  started: number;
  expired_not_started: number;
  finished_started: number;
  expired_started: number;
  started_with_interaction: number;
  finished: number;
  succeeded_finished: number;
  rejected_finished: number;
  pending_finished: number;
  ever_marked_for_review_finished: number;
  auto_finished: number;
  manual_finished: number;
  marked_for_review: number;
  decisioned_marked_for_review: number;
  approved_decisioned_marked_for_review: number;
  updated_at?: Iso8601DateString;
}

export interface MetricsInquiryVolume extends MetricsInquiryVolumeMetrics {
  template_uses_decisioning: boolean;
  domain:
    | {
        domain_start: Iso8601DateString;
        domain_end: Iso8601DateString;
      }
    | undefined;
}

export type MetricsInquiryRecentActivityVolume = Pick<
  MetricsInquiryVolume,
  'created' | 'started' | 'finished' | 'succeeded_finished' | 'finished_started' | 'domain'
>;

export interface MetricsInquiryVolumeDatum extends MetricsInquiryVolumeMetrics {
  created_timestamp: UnixTime;
}

export interface MetricsInquiryMedianCompletionDatum {
  pct_50th: number;
  pct_75th: number;
  pct_90th: number;
}

export interface MetricsInquiryVolumeOverTime {
  data: MetricsInquiryVolumeDatum[];
  domain: [UnixTime | null, UnixTime];
  period: Period;
}

export interface MetricsInquiryMedianCompletion {
  data: MetricsInquiryMedianCompletionDatum;
}

export interface InquiryFilterOptions {
  country_codes: string[];
  device_groups: string[];
  platforms: string[];
}

export interface MetricsTemplateFunnelFlow {
  token?: string;
  updated_at?: string;
  steps: Array<MetricsTemplateFunnelFlowStep>;
  branches: Array<MetricsTemplateFunnelFlow>;
  condition?: {
    name: string;
    token: string;
  };
}

export interface MetricsTemplateFunnelFlowPayload {
  funnelFlows: MetricsTemplateFunnelFlow[];
  queriedAt: string;
  inquiryTemplateId: string;
  updatedAt: string;
}

export interface MetricsTemplateLoadedTemplateIdPayload {
  templateId: string;
}

export interface MetricsPeriodicityPayload {
  granularities: InquiryGranularities;
}

export interface MetricsInquiryTemplateVersionLoadedIdPayload {
  inquiryTemplateVersionId: string;
}

export interface MetricsTemplateFunnelFlowPercentage {
  relative: number;
  absolute: number;
  relative_base: number;
  absolute_base: number;
}

interface MetricsTemplateFunnelFlowMetrics {
  created_timestamp: UnixTime;
  value: number;
  percentage: MetricsTemplateFunnelFlowPercentage;
}

interface MetricsTemplateFunnelConversion {
  id: string;
  name: string;
  condition: string;
  data: Array<MetricsTemplateFunnelFlowMetrics>;
}

export interface MetricsTemplateFunnelConversionOverTime {
  data: Array<MetricsTemplateFunnelConversion>;
  domain: [UnixTime, UnixTime];
  period: Period;
}

export interface MetricsTemplateFunnelConversionOverTimePayload {
  funnelConversionOverTime: MetricsTemplateFunnelConversionOverTime;
  queriedAt: string;
}
